import { type PropsWithChildren, useEffect, useRef } from 'react';

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useTheme } from '@/_shared';

import type { ModalProps } from './types';

import { Button } from '../button';

export const Modal = (props: PropsWithChildren<ModalProps>) => {
  const {
    children,
    onClose,
    open,
    className,
    disableInternalClosing = false,
    showBackdropCloseButton = false,
    disableBackdropClosing = false,
    closeOnEscape = false,
    hideCloseButton = false,
    containerWrapperClassName = '',
    containerClassName = '',
    size = 'md',
    ...other
  } = props;

  const { modal, backdrop } = useTheme();
  const cls = twMerge(
    modal.base,
    size === 'lg'
      ? 'w-full min-w-full outline-hidden md:w-5/6 md:max-w-[83.333333%] md:min-w-[83.333333%] lg:w-3/4 lg:max-w-[75%] lg:min-w-[75%] 2xl:w-3/5 2xl:max-w-[60%] 2xl:min-w-[60%]'
      : '',
    className
  );

  const handleClose = () => {
    if (!disableBackdropClosing) {
      handleCloseButton();
    }
  };

  const handleCloseButton = () => {
    if (!disableInternalClosing) {
      onClose(false);
    }
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !closeOnEscape || disableInternalClosing) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      const dialogs = document.querySelectorAll('[role="dialog"]');
      const topDialog = dialogs[dialogs.length - 1];
      if (topDialog !== dialogRef.current) return;

      event.preventDefault();
      event.stopPropagation();

      if (!disableInternalClosing) {
        onClose(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, closeOnEscape, disableInternalClosing, onClose]);

  return (
    <Dialog
      ref={dialogRef}
      as="div"
      transition
      className="relative inset-0 z-20 flex w-screen items-center justify-center bg-black/30 p-4 data-closed:opacity-0"
      onClose={handleClose}
      open={open}
    >
      <DialogBackdrop className={backdrop.base} />
      {open && showBackdropCloseButton && (
        <div className="fixed top-5 right-6 z-9999">
          <Button
            icon={X}
            size="base"
            className="border-c-border-muted bg-black/90 hover:scale-105 md:border-transparent"
            theme="dark"
            onClick={handleCloseButton}
            aria-label="Close"
          />
        </div>
      )}
      <section className={twMerge('fixed inset-0 z-40 overflow-y-auto', containerWrapperClassName)}>
        <div
          className={twMerge(
            'flex min-h-full items-end justify-center md:items-center md:justify-center',
            containerClassName
          )}
        >
          <DialogPanel {...other} className={cls}>
            {!hideCloseButton && !showBackdropCloseButton && (
              <Button
                icon={X}
                size="sm"
                as="link"
                disabled={disableInternalClosing}
                aria-label="close"
                onClick={handleCloseButton}
                className="absolute! top-4 right-4"
              />
            )}
            {children}
          </DialogPanel>
        </div>
      </section>
    </Dialog>
  );
};
